/**
* Copyright by: 

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { Injectable, OnDestroy } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  Router,
  RouterStateSnapshot,
  UrlTree
} from '@angular/router';
import { Observable, Subscription, filter, isObservable, map } from 'rxjs';
import { CanComponentDeactivate } from '../../app/shared/interface/can-component-deactivate.interface';

@Injectable({
  providedIn: 'root'
})
export class CandeactivateGuard implements OnDestroy {
  private confirmedTargets = new Map<CanComponentDeactivate, string>();
  private subscription: Subscription;

  constructor(private router: Router) {
    this.subscription = this.router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationEnd ||
            (event instanceof NavigationCancel && event.code !== NavigationCancellationCode.Redirect)
        )
      )
      .subscribe(() => this.confirmedTargets.clear());
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  canDeactivate(
    component: CanComponentDeactivate,
    currentRoute: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot
  ): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    const targetPath = this.router.parseUrl(nextState?.url ?? currentState.url).root.children['primary']?.toString() ?? '';

    if (this.confirmedTargets.get(component) === targetPath) {
      return true;
    }

    const result = component.canDeactivate(nextState?.url ?? currentState.url);

    if (result instanceof Promise) {
      return result.then((value) => {
        if (value) {
          this.confirmedTargets.set(component, targetPath);
        }
        return value;
      });
    }

    if (isObservable(result)) {
      return result.pipe(
        map((value) => {
          if (value) {
            this.confirmedTargets.set(component, targetPath);
          }
          return value;
        })
      );
    }

    if (result) {
      this.confirmedTargets.set(component, targetPath);
    }

    return result;
  }
}
